/**
 * IndustryNotificationBell — soft pill in the Mission Control header
 * surfacing unread Industry Library notifications for super admins.
 *
 * Notifications fire when *another* super admin mutates a profile that
 * this user either created (auto-watch) or explicitly opted in to via
 * the per-card "Watch" toggle. The bell closes the loop on multi-admin
 * Industry Library workflows where coefficient changes can quietly
 * cascade into ROI math for hundreds of tenants.
 *
 * Renders only when the current user is a super admin AND has at least
 * one notification (read or unread) so it never adds visual noise for
 * tenants who never tweak the library.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../../lib/authedFetch';
import { Bell, BellRing, Pencil, Archive, Trash2, MoveRight, Users, Check } from 'lucide-react';
import { useHeaderTheme } from './HeaderThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

// 30s poll keeps the badge fresh during a session of admin work
// without hammering Mongo. Tab visibility could shorten this further
// in the future — for now 30s is a good cost/responsiveness tradeoff.
const POLL_MS = 30000;

const ACTION_ICON = {
  updated: Pencil,
  archived: Archive,
  hard_deleted: Trash2,
  org_reassigned_in: MoveRight,
  org_cleared: Users,
};

const ACTION_LABEL = {
  updated: 'edited',
  archived: 'archived',
  hard_deleted: 'deleted',
  org_reassigned_in: 'moved an org into',
  org_cleared: 'cleared the industry tag on an org',
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function actorLocalPart(email) {
  if (!email) return 'someone';
  return String(email).split('@')[0] || email;
}

export default function IndustryNotificationBell({ isSuperAdmin }) {
  const { onDark } = useHeaderTheme();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  const fetchInbox = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/notifications?limit=20`);
      if (!res.ok) return;
      const data = await res.json();
      setItems(data?.notifications || []);
      setUnread(Number.isFinite(data?.unread_count) ? data.unread_count : 0);
    } catch {
      // Inbox is best-effort — never let it break the header.
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    fetchInbox();
    const handle = setInterval(fetchInbox, POLL_MS);
    return () => clearInterval(handle);
  }, [isSuperAdmin, fetchInbox]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (id) => {
    // Optimistic flip — the row is greyed immediately so the dropdown
    // feels responsive even on slow connections.
    setItems((prev) => prev.map((n) => (n.id === id && !n.read_at
      ? { ...n, read_at: new Date().toISOString() }
      : n)));
    setUnread((c) => Math.max(0, c - 1));
    try {
      await authedFetch(`${API}/api/industry-profiles/notifications/${id}/read`, { method: 'POST' });
    } catch {
      // Best-effort — next poll will reconcile if the optimistic
      // update diverged from server state.
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
    setUnread(0);
    try {
      await authedFetch(`${API}/api/industry-profiles/notifications/read-all`, { method: 'POST' });
    } catch { /* noop */ }
  };

  const jump = async (n) => {
    if (!n.read_at) await markRead(n.id);
    setOpen(false);
    navigate('/admin/industry-library');
    // Use a microtask so the route swap finishes before we try to
    // scroll the card into view.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="industry-row-${n.industry_id}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-indigo-400');
        setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-400'), 1500);
      }
    }, 200);
  };

  if (!isSuperAdmin) return null;
  // Hide the bell entirely when there's nothing to show — keeps the
  // header from accruing chrome that does nothing.
  if (items.length === 0 && unread === 0) return null;

  const BellIcon = unread > 0 ? BellRing : Bell;

  return (
    <div className="relative" ref={wrapperRef} data-testid="industry-notification-bell">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`relative inline-flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium ${onDark ? 'border-[#1f2a44] bg-white/5 text-slate-200 hover:bg-white/10' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'} ${unread > 0 ? 'ring-1 ring-indigo-400' : ''}`}
        data-testid="industry-notification-bell-trigger"
        aria-label={unread > 0 ? `${unread} unread industry notifications` : 'Industry notifications'}
      >
        <BellIcon className={`h-3.5 w-3.5 ${unread > 0 ? (onDark ? 'text-indigo-400' : 'text-indigo-600') : (onDark ? 'text-slate-400' : 'text-slate-500')}`} />
        Industry
        {unread > 0 && (
          <span
            className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-indigo-600 text-white text-[10px] font-bold"
            data-testid="industry-notification-bell-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-h-[480px] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl z-50"
          data-testid="industry-notification-bell-dropdown"
        >
          <div className="sticky top-0 bg-white border-b border-slate-100 px-3 py-2 flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-900">Industry Library activity</div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[10px] text-indigo-600 hover:underline"
                data-testid="industry-notification-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-500">
              You&apos;re all caught up. Watch a profile to start getting alerts when another admin edits it.
            </div>
          ) : (
            <ul data-testid="industry-notification-bell-list">
              {items.map((n) => {
                const Icon = ACTION_ICON[n.action] || Pencil;
                const verb = ACTION_LABEL[n.action] || 'updated';
                const isUnread = !n.read_at;
                return (
                  <li
                    key={n.id}
                    className={`border-b border-slate-50 last:border-b-0 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${isUnread ? 'bg-indigo-50/30' : ''}`}
                    onClick={() => jump(n)}
                    data-testid={`industry-notification-row-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {isUnread && (
                        <span className="shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full bg-indigo-500" data-testid={`industry-notification-dot-${n.id}`} />
                      )}
                      {!isUnread && (
                        <span className="shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full bg-transparent" />
                      )}
                      <div className="rounded-md bg-slate-100 p-1 shrink-0 mt-0.5">
                        <Icon className="h-3 w-3 text-slate-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-800 leading-snug">
                          <span className="font-semibold">{actorLocalPart(n.actor_email)}</span>
                          {' '}{verb}{' '}
                          <span className="font-semibold">{n.industry_name || n.industry_id}</span>
                          {n.org_name && (n.action === 'org_reassigned_in' || n.action === 'org_cleared') && (
                            <span> — {n.org_name}</span>
                          )}
                        </p>
                        {n.summary && (
                          <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{n.summary}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-0.5">{relativeTime(n.created_at)}</p>
                      </div>
                      {isUnread && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                          className="shrink-0 text-slate-400 hover:text-emerald-600"
                          data-testid={`industry-notification-mark-read-${n.id}`}
                          aria-label="Mark this notification as read"
                          title="Mark as read"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
